import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Caravan, LogOut, ChevronDown, User } from 'lucide-react';
import { useState } from 'react';
import { useAuthStore } from '../../store/useAuthStore';
import { BRAND_NAME } from '../../lib/brand';

/**
 * Role-aware application shell. Navigation is derived from the permissions the
 * server returned, not from a hardcoded per-role list in the client, so adding
 * a capability server-side surfaces here without a second edit.
 */
export default function AppShell({ nav = [], aside = null, children }) {
  /**
   * An entry may carry `when`, a permission key. A platform admin works across
   * two consoles, so each needs a way back to the other — but the operator
   * consoles must not sprout a Platform tab for the owner, who cannot open it.
   */
  const permissions = useAuthStore((s) => s.permissions);
  const visible = nav.filter((item) => !item.when || permissions?.[item.when]);
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const signOut = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-30 shrink-0 border-b border-slate-200 bg-white">
        <div className="flex items-center gap-6 px-4 py-3 sm:px-6">
          <Link to="/" state={{ landing: true }} aria-label={`${BRAND_NAME} home`}
                className="flex shrink-0 items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-lg bg-brand-600 text-white">
              <Caravan className="h-4 w-4" aria-hidden="true" />
            </span>
            <span className="hidden text-base font-bold tracking-tight text-slate-900 sm:block">{BRAND_NAME}</span>
          </Link>

          <nav className="flex min-w-0 flex-1 items-center gap-1 overflow-x-auto" aria-label="Main">
            {visible.map(({ to, label, Icon, end }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                className={({ isActive }) =>
                  `inline-flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold transition ${
                    isActive ? 'bg-brand-50 text-brand-800' : 'text-slate-600 hover:bg-slate-100'
                  }`
                }
              >
                {Icon && <Icon className="h-4 w-4" aria-hidden="true" />}
                {label}
              </NavLink>
            ))}
          </nav>

          {aside && <div className="hidden shrink-0 md:block">{aside}</div>}

          <div className="relative shrink-0">
            <button
              type="button"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-expanded={menuOpen}
              className="flex items-center gap-2 rounded-full border border-slate-300 py-1.5 pl-3 pr-2 text-sm font-semibold text-slate-800 transition hover:border-slate-400"
            >
              <span className="hidden sm:inline">{user?.first_name}</span>
              <RoleBadge role={user?.role} />
              <ChevronDown className={`h-4 w-4 text-brand-500 transition ${menuOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
            </button>

            {menuOpen && (
              <div className="absolute right-0 z-40 mt-2 w-56 rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl">
                <div className="border-b border-slate-100 px-3 py-2">
                  <p className="truncate text-sm font-bold text-slate-900">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="truncate text-xs text-slate-500">{user?.email}</p>
                </div>
                <Link
                  to="/profile"
                  onClick={() => setMenuOpen(false)}
                  className="mt-1 flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                >
                  <User className="h-4 w-4 text-brand-500" aria-hidden="true" />
                  Your account
                </Link>
                <button
                  type="button"
                  onClick={signOut}
                  className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-semibold text-slate-700 transition hover:bg-slate-100"
                >
                  <LogOut className="h-4 w-4 text-brand-500" aria-hidden="true" />
                  Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="min-h-0 flex-1 bg-slate-50">{children}</main>
    </div>
  );
}

const ROLE_STYLES = {
  customer: 'bg-slate-100 text-slate-700',
  attendant: 'bg-sky-100 text-sky-800',
  admin: 'bg-brand-100 text-brand-800',
  super_admin: 'bg-amber-100 text-amber-900',
};

const ROLE_LABELS = {
  customer: 'Tenant', attendant: 'Attendant', admin: 'Admin', super_admin: 'Owner',
};

export function RoleBadge({ role }) {
  if (!role) return null;
  return (
    <span className={`rounded-full px-2 py-0.5 text-[12px] font-bold ${ROLE_STYLES[role] ?? ROLE_STYLES.customer}`}>
      {ROLE_LABELS[role] ?? role}
    </span>
  );
}
